import React, { useMemo, useState, useEffect, useRef } from 'react'

import { ChevronLeft, ChevronRight, Download, Play, X } from 'lucide-react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import writeXlsxFile from 'write-excel-file'

import coverImageBlack from '@/assets/images/logo_isotipo_black.png'
import coverImageWhite from '@/assets/images/logo_isotipo_white.png'
import { Event } from '@/core/api/entities/zone/event'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import {
	resetChatbaseLauncherBottomOffset,
	setChatbaseLauncherBottomOffset,
} from '@/shared/analytics/ChatbaseWidget'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Search } from '@/shared/components/molecules/Search/Search'
import { shouldRenderMobileDrawerMenu } from '@/shared/components/organisms/Layout/MobileDrawerMenu/MobileDrawerMenu'
import { DesktopVolumeControl } from '@/shared/components/organisms/Player/DesktopVolumeControl'
import Player from '@/shared/components/organisms/Player/Player'
import { useScrollDirection } from '@/shared/hooks/useScrollDirection'
import { cn } from '@/shared/utils/helpers'

interface PlaybackHistoryTabProps {
	zone: Zone
	events: Event[]
	isLoading?: boolean
	error?: Error | null
}

const ITEMS_PER_PAGE = 15

export const PlaybackHistoryTab: React.FC<PlaybackHistoryTabProps> = ({
	zone,
	events,
	isLoading = false,
	error = null,
}) => {
	const [searchTerm, setSearchTerm] = useState('')
	const [currentPage, setCurrentPage] = useState(1)
	const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
	const [isMuted, setIsMuted] = useState(false)
	const [volume, setVolume] = useState(1)
	const audioElementRef = useRef<HTMLAudioElement | null>(null)
	const location = useLocation()
	const isMobileNavVisible = useScrollDirection({
		threshold: 8,
		topThreshold: 16,
	})
	const shouldShowMobileDrawer = shouldRenderMobileDrawerMenu(location.pathname)
	const shouldOffsetForMobileDrawer =
		shouldShowMobileDrawer && isMobileNavVisible

	// Get ui state from Redux for date formatting and player cover variant
	const { language, theme } = useSelector((state: RootState) => state.ui)
	const playerCoverImage = theme === 'light' ? coverImageBlack : coverImageWhite

	// Get translation function
	const { t } = useTranslation('common')

	// Filter events based on search
	const filteredEvents = useMemo(() => {
		if (!searchTerm) return events

		const lowerSearch = searchTerm.toLowerCase()
		return events.filter(
			(event) =>
				event.eventName.toLowerCase().includes(lowerSearch) ||
				event.description?.toLowerCase().includes(lowerSearch) ||
				event.origin.title?.toLowerCase().includes(lowerSearch) ||
				event.origin.segmentName?.toLowerCase().includes(lowerSearch)
		)
	}, [events, searchTerm])

	// Pagination
	const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE)
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
	const endIndex = startIndex + ITEMS_PER_PAGE
	const currentEvents = filteredEvents.slice(startIndex, endIndex)

	// Reset to page 1 when search changes
	useEffect(() => {
		setCurrentPage(1)
	}, [searchTerm])

	// Match /music: keep Chatbase launcher above bottom player while visible.
	useEffect(() => {
		if (selectedEvent) {
			setChatbaseLauncherBottomOffset(140)
			return () => resetChatbaseLauncherBottomOffset()
		}
		resetChatbaseLauncherBottomOffset()
	}, [selectedEvent])

	const getEventType = (event: Event): string => {
		// Determine event type based on eventName
		if (event.eventName.includes('song')) {
			return t('entities.zone.event.song')
		}
		if (
			event.eventName.includes('advert') ||
			event.eventName.includes('announcement')
		) {
			return t('entities.zone.event.advert')
		}
		return 'Event'
	}

	const formatDate = (dateTime: any): string => {
		const format = language.startsWith('en')
			? 'MM/dd/yyyy HH:mm'
			: 'dd/MM/yyyy HH:mm'
		return dateTime.toFormat(format) + 'hs'
	}

	const handlePlay = (event: Event) => {
		setSelectedEvent(event)
	}

	const handleClosePlayer = () => {
		audioElementRef.current?.pause()
		setSelectedEvent(null)
	}

	const getEventTitle = (event: Event): string => {
		return (
			event.origin.announcementName ||
			event.description ||
			event.origin.title ||
			t('time.unknown')
		)
	}

	const getEventArtist = (event: Event): string => {
		return event.origin.title || event.origin.segmentName || ''
	}

	const handleDownload = async () => {
		const schema = [
			{
				column: t('entities.zone.last_events.columns.artist') || 'Artist',
				type: String,
				value: (event: Event) =>
					event.origin.title || event.origin.segmentName || '',
			},
			{
				column: t('entities.zone.last_events.columns.eventName') || 'Title',
				type: String,
				value: (event: Event) =>
					event.origin.announcementName ||
					event.description ||
					event.origin.title ||
					'',
			},
			{
				column: t('entities.zone.last_events.columns.date') || 'Date',
				type: String,
				value: (event: Event) => formatDate(event.createdAt),
			},
		]

		await writeXlsxFile(filteredEvents, {
			schema,
			fileName: `events_${zone?.id}_${new Date().toISOString().split('T')[0]}.xlsx`,
		})
	}

	const renderPagination = () => {
		const pages = []
		const maxPagesToShow = 5

		let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2))
		let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

		if (endPage - startPage < maxPagesToShow - 1) {
			startPage = Math.max(1, endPage - maxPagesToShow + 1)
		}

		for (let i = startPage; i <= endPage; i++) {
			pages.push(
				<Button
					key={i}
					variant={currentPage === i ? 'default' : 'ghost'}
					size="sm"
					onClick={() => setCurrentPage(i)}
					className={
						currentPage === i
							? 'bg-white text-black hover:bg-gray-100'
							: 'text-muted-foreground hover:text-foreground'
					}
				>
					{i}
				</Button>
			)
		}

		return (
			<div className="flex items-center justify-center gap-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
					disabled={currentPage === 1}
					className="text-muted-foreground hover:text-foreground"
				>
					<ChevronLeft className="h-4 w-4 mr-1" />
					{t('common.prev')}
				</Button>

				{startPage > 1 && (
					<>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setCurrentPage(1)}
							className="text-muted-foreground hover:text-foreground"
						>
							1
						</Button>
						{startPage > 2 && (
							<span className="text-muted-foreground px-2">...</span>
						)}
					</>
				)}

				{pages}

				{endPage < totalPages && (
					<>
						{endPage < totalPages - 1 && (
							<span className="text-muted-foreground px-2">...</span>
						)}
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setCurrentPage(totalPages)}
							className="text-muted-foreground hover:text-foreground"
						>
							{totalPages}
						</Button>
					</>
				)}

				<Button
					variant="ghost"
					size="sm"
					onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
					disabled={currentPage === totalPages}
					className="text-muted-foreground hover:text-foreground"
				>
					{t('actions.next')}
					<ChevronRight className="h-4 w-4 ml-1" />
				</Button>
			</div>
		)
	}

	return (
		<div className={`space-y-6 ${selectedEvent ? 'pb-36' : ''}`}>
			{/* Error State */}
			{error && (
				<div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
					<p className="text-sm text-red-400">
						{t('entities.zone.last_events.error', { error: error.message })}
					</p>
				</div>
			)}

			{/* Search & Download */}
			<div className="flex items-center justify-between gap-4">
				<Search
					value={searchTerm}
					onChange={setSearchTerm}
					placeholder={t('entities.zone.last_events.search_placeholder')}
					className="max-w-md"
				/>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						onClick={handleDownload}
						className="flex items-center gap-2"
						disabled={filteredEvents.length === 0}
					>
						<Download className="h-4 w-4" />
						{t('actions.download')}
					</Button>
				</div>
			</div>

			{/* Table */}
			<div className="border border-border rounded-lg overflow-hidden">
				{/* Table Header */}
				<div className="grid grid-cols-[1fr_150px_200px] gap-4 px-6 py-4 bg-muted border-b border-border">
					<div className="text-sm font-medium text-muted-foreground">
						{t('entities.zone.last_events.columns.eventName')}
					</div>
					<div className="text-sm font-medium text-muted-foreground">
						{t('entities.zone.last_events.columns.type')}
					</div>
					<div className="text-sm font-medium text-muted-foreground">
						{t('entities.zone.last_events.columns.date')}
					</div>
				</div>

				{/* Table Body */}
				<div className="divide-y divide-border">
					{isLoading ? (
						<div className="px-6 py-12 text-center text-muted-foreground">
							{t('entities.zone.last_events.loading')}
						</div>
					) : currentEvents.length === 0 ? (
						<div className="px-6 py-12 text-center text-muted-foreground">
							{searchTerm
								? t('entities.zone.last_events.empty.search')
								: t('entities.zone.last_events.empty.default')}
						</div>
					) : (
						currentEvents.map((event, index) => {
							const eventType = getEventType(event)

							return (
								<div
									key={index}
									className="grid grid-cols-[1fr_150px_200px] gap-4 px-6 py-4 hover:bg-muted transition-colors group"
								>
									{/* Event column */}
									<div className="flex items-center gap-3 min-w-0">
										<div
											className={`flex-shrink-0 h-10 w-10 bg-shade rounded flex items-center justify-center ${
												event.isPlayable()
													? 'cursor-pointer hover:bg-accent transition-colors'
													: ''
											}`}
											onClick={
												event.isPlayable() ? () => handlePlay(event) : undefined
											}
										>
											<Play
												className={`h-5 w-5 ${
													event.isPlayable() ? '' : 'text-muted-foreground'
												}`}
											/>
										</div>
										<div className="flex-1 min-w-0">
											<div className="text-sm font-medium truncate">
												{event.origin.announcementName ||
													event.description ||
													event.origin.title}
											</div>
											<div className="text-xs text-muted-foreground truncate">
												{event.origin.title || event.origin.segmentName}
											</div>
										</div>
									</div>

									{/* Type column */}
									<div className="flex items-center">
										<Badge variant="outline" className={'text-xs'}>
											{eventType}
										</Badge>
									</div>

									{/* Date column */}
									<div className="flex items-center text-sm text-muted-foreground">
										{formatDate(event.createdAt)}
									</div>
								</div>
							)
						})
					)}
				</div>
			</div>

			{/* Pagination */}
			{!isLoading && filteredEvents.length > 0 && totalPages > 1 && (
				<div className="flex justify-center pt-4">{renderPagination()}</div>
			)}

			{/* Results info */}
			{!isLoading && filteredEvents.length > 0 && (
				<div className="text-center text-xs text-muted-foreground">
					{t('common.showing', {
						start: startIndex + 1,
						end: Math.min(endIndex, filteredEvents.length),
						total: filteredEvents.length,
					})}
				</div>
			)}

			{/* Player (match /music layout) */}
			{selectedEvent && (
				<div
					className={cn(
						'fixed md:bottom-0 left-0 right-0 lg:left-20 z-[1000] transition-[bottom] duration-300 ease-out',
						shouldOffsetForMobileDrawer ? 'bottom-23' : 'bottom-0'
					)}
				>
					<div className="w-full 2xl:px-6 md:px-3">
						<div className="mx-auto w-full max-w-[1400px]">
							<div className="relative player-mobile-shell dark:md:bg-card md:bg-secondary md:border lg:rounded-b-[8px] md:px-6 md:py-4 lg:py-6 flex items-center lg:h-[120px]">
								<div className="w-full flex items-center gap-4 pr-10">
									<Player
										key={`${selectedEvent.playerId}-${selectedEvent.createdAt?.toISO?.() ?? ''}-${selectedEvent.path ?? ''}`}
										src={selectedEvent.path || ''}
										title={getEventTitle(selectedEvent)}
										radioTitle={zone?.name || ''}
										metadata={[getEventArtist(selectedEvent)].filter(Boolean)}
										disablePrevious={true}
										disableNext={true}
										showSkipControls={false}
										autoPlay={true}
										hideVolume={true}
										compact={true}
										audioRef={(el) => {
											audioElementRef.current = el
											if (el) {
												el.volume = isMuted ? 0 : volume
											}
										}}
										cssVars={{
											border: 'none',
											backgroundColor: 'transparent',
										}}
										cover={playerCoverImage}
									/>
									<div className="lg:flex hidden items-center gap-6 shrink-0">
										<DesktopVolumeControl
											isMuted={isMuted}
											setIsMuted={setIsMuted}
											volume={volume}
											setVolume={setVolume}
											audioElementRef={audioElementRef}
										/>
									</div>
								</div>
								<Button
									variant="ghost"
									size="icon"
									onClick={handleClosePlayer}
									className="absolute top-3 right-3"
									aria-label={t('actions.close')}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
